import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useUpdateAirtableInfoMutation,
	useLazyGetAirtableBasesQuery,
	useLazyGetAirtableTablesQuery,
	useDisconnectAirtableMutation,
	useLazyGetAirtableConnectionStatusQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import SelectSkeleton from '../SelectSkeleton';
import AirtableIcon from '../../../assets/images/airtable.svg';

interface Table {
	id: string;
	name: string;
}

interface Base {
	id: string;
	name: string;
}

const AirtableSelect: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [updateAirtableInfo, { isLoading }] = useUpdateAirtableInfoMutation();
	const [getBases, { data: basesData }] = useLazyGetAirtableBasesQuery();
	const [getTables, { data: tablesData }] = useLazyGetAirtableTablesQuery();
	const [disconnectAirtable] = useDisconnectAirtableMutation();
	const [
		getConnectionStatus,
		{ data: connectionStatus },
	] = useLazyGetAirtableConnectionStatusQuery();
	const user = useCurrentUser();
	const [selectedBase, setSelectedBase] = useState<string>('');
	const [selectedTable, setSelectedTable] = useState<string>('');
	const [isLoadingBases, setIsLoadingBases] = useState(false);

	const params = new URLSearchParams(location.search)
	const companyId = params.get('companyId') || user?.companyId || ''

	useEffect(() => {
		if (companyId) {
			getConnectionStatus(companyId)
				.unwrap()
				.then((res: any) => {
					if (res?.bases?.length > 0 && !selectedBase) {
						setSelectedBase(res.bases[0].projectId)
						if (res.bases[0].tableId) {
							setSelectedTable(res.bases[0].tableId)
						}
					}
				})
				.catch(() => {})
		}
	}, [companyId, getConnectionStatus])

	useEffect(() => {
		if (companyId && !selectedBase) {
			setIsLoadingBases(true)
			getBases(companyId)
				.unwrap()
				.then((res: any) => {
					if (res?.bases?.length > 0 && !selectedBase) {
						setSelectedBase(res.bases[0].id)
					}
				})
				.catch(() => {})
				.finally(() => setIsLoadingBases(false))
		}
	}, [companyId, selectedBase, getBases])

	useEffect(() => {
		if (selectedBase && companyId) {
			getTables({ companyId, baseId: selectedBase })
		}
	}, [selectedBase, companyId])

	const handleSubmit = async () => {
		if (!selectedTable || !selectedBase || !companyId) {
			ReactToastify({
				success: false,
				message: 'Base or table missing',
			});
			return;
		}

		try {
			await updateAirtableInfo({
				companyId,
				data: {
					isAirtable: true,
					baseId: selectedBase,
					tableId: selectedTable,
				},
			}).unwrap();

			ReactToastify({
				success: true,
				message: 'Airtable info updated successfully',
			});

			navigate('/dashboard/ask-tara');
		} catch (err) {
			console.error('Airtable update failed:', err);
			ReactToastify({
				success: false,
				message: 'Something went wrong while saving Airtable info',
			});
		}
	};

	const handleDisconnect = async () => {
		if (!companyId) return
		try {
			await disconnectAirtable(companyId).unwrap()
			setSelectedBase('')
			setSelectedTable('')
			ReactToastify({ success: true, message: 'Airtable disconnected' })
		} catch (err) {
			ReactToastify({ success: false, message: 'Failed to disconnect Airtable' })
		}
	}

  return (
		<div className="relative w-full h-screen  overflow-hidden font-inter">
			<div className="flex items-center justify-center flex-col h-full">
				<div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
					<div className="flex items-center justify-between mb-6">
						<div className="flex items-center gap-3">
							<img src={AirtableIcon} alt="Airtable" className="h-8 w-8" />
							<h1 className="text-2xl font-bold">Airtable</h1>
						</div>
						<button
							type="button"
							onClick={handleDisconnect}
							className="px-3 py-1.5 text-red-600 border border-red-600 rounded hover:bg-red-50 transition text-sm"
						>
							Disconnect
						</button>
					</div>

					{isLoadingBases || !basesData ? (
						<SelectSkeleton />
					) : (
						<>
							<div>
								<label className="my-2 text-xs font-roboto pl-2 block">
									Select Base
								</label>
								<select
									value={selectedBase}
									onChange={(e) => {
										setSelectedBase(e.target.value)
										setSelectedTable('')
									}}
									className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
								>
									{basesData?.bases?.map((base: Base) => (
										<option key={base.id} value={base.id}>
											{base.name}
										</option>
									))}
								</select>
							</div>

							<div>
								<label className="my-2 text-xs font-roboto pl-2 block">
									Select Table
								</label>
								<select
									value={selectedTable}
									onChange={(e) => setSelectedTable(e.target.value)}
									className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
								>
									<option value="">Select a Table</option>
									{tablesData?.tables?.map((table: Table) => (
										<option key={table.id} value={table.id}>
											{table.name}
										</option>
									))}
								</select>
							</div>

							<div className="flex justify-end items-center mt-4">
								<button
									type="button"
									onClick={handleSubmit}
									disabled={isLoading || !selectedTable}
									className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center p-2 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default AirtableSelect;
